import * as _ from 'lodash';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import { funding as calcAgentFunding } from '@finley-technologies/calc-agent';
import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Loan, Util } from '@finley-technologies/finley-types';

import { useGetOrCreateFundingRequestTemplateQuery } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
/**
 * ==================================
 * Types picked off calcAgentFunding.FundingRequest
 * ==================================
 */

/**
 * ==================================
 * Getter Fn Types
 * ==================================
 */

type GetFunctionPricingOption = calcAgentFunding.FundingRequestGenerator['data']['pricingOptions']['get'];

type GetFunctionRecycleRequest = calcAgentFunding.FundingRequestGenerator['data']['recycleRequests']['get'];

type GetFunctionNetRecycleRequest = calcAgentFunding.FundingRequestGenerator['data']['netRecycleRequest']['get'];

type GetFunctionDrawRequest = calcAgentFunding.FundingRequestGenerator['data']['drawRequests']['get'];

type GetFunctionExpectedSubmission = calcAgentFunding.FundingRequestGenerator['data']['expectedSubmission']['get'];

type GetFunctionFundingImpact = calcAgentFunding.FundingRequestGenerator['data']['fundingImpact']['get'];

type GetFunctionMaturityDate = calcAgentFunding.FundingRequestGenerator['data']['maturityDate']['get'];

type GetFunctionExternalLoanId = calcAgentFunding.FundingRequestGenerator['data']['externalLoanId']['get'];

type GetFunctionLoanType = calcAgentFunding.FundingRequestGenerator['data']['loanType']['get'];

type GetFunctionResolvedLoan = calcAgentFunding.FundingRequestGenerator['data']['resolvedLoan']['get'];

type GetFunction =
  | GetFunctionPricingOption
  | GetFunctionRecycleRequest
  | GetFunctionNetRecycleRequest
  | GetFunctionDrawRequest
  | GetFunctionExpectedSubmission
  | GetFunctionFundingImpact
  | GetFunctionMaturityDate
  | GetFunctionExternalLoanId
  | GetFunctionLoanType
  | GetFunctionResolvedLoan;

/**
 * ==================================
 * Getter Fn Return Types
 * ==================================
 */

/**
 * return type for FundingRequest.data.recycleRequests.get
 */
export type RecycleRequest = ReturnType<GetFunctionRecycleRequest>[number];

export type NetRecycleRequest = ReturnType<GetFunctionNetRecycleRequest>;

/**
 * return type for FundingRequest.data.pricingOptions.get
 */
export type DrawRequest = ReturnType<GetFunctionDrawRequest>[number];

/**
 * ==================================
 * Setter Fn Types
 * ==================================
 */

type SetFunctionPricingOptionsFn = calcAgentFunding.FundingRequestGenerator['data']['pricingOptions']['set'];

type SetFunctionRecycleRequestsFn = calcAgentFunding.FundingRequestGenerator['data']['recycleRequests']['set'];

type SetFunctionDrawRequestsFn = calcAgentFunding.FundingRequestGenerator['data']['drawRequests']['set'];

type SetFunctionTargetFundingDateFn = calcAgentFunding.FundingRequestGenerator['data']['targetFundingDate']['set'];

type SetFunctionExternalLoanIdFn = calcAgentFunding.FundingRequestGenerator['data']['externalLoanId']['set'];

type SetMaturityDateFn = calcAgentFunding.FundingRequestGenerator['data']['maturityDate']['set'];

type SetFunctionLoanTypeFn = calcAgentFunding.FundingRequestGenerator['data']['loanType']['set'];

// type of setter returned by funding.getFundingRequest()[data][property].set
type SetFunction =
  | SetFunctionPricingOptionsFn
  | SetFunctionRecycleRequestsFn
  | SetFunctionDrawRequestsFn
  | SetFunctionTargetFundingDateFn
  | SetFunctionExternalLoanIdFn
  | SetMaturityDateFn
  | SetFunctionLoanTypeFn;

export type ContextValueMutable = {
  mode: 'create' | 'upsize';
  allowedActions: 'mutable';
  // ***** Static properties *****
  creditAgreementId: string;
  summary: calcAgentFunding.Summary;

  // ***** UI Flags *****

  reportingMechanics: calcAgentFunding.FundingReportingMechanics;

  // ***** Request data *****

  data: {
    pricingOptions: calcAgentFunding.PricingOption[];
    // rename to recycle requests?
    recycleRequests: RecycleRequest[];
    netRecycleRequest: NetRecycleRequest;
    // rename to drawRequests?
    drawRequests: DrawRequest[];
    expectedSubmission: calcAgentFunding.ExpectedSubmission;
    fundingImpact: calcAgentFunding.FundingImpact;
    maturityDate: Util.FullDateStr;
    externalLoanId?: string;
    loanType: Loan.LoanType;
    resolvedLoan?: Loan.ResolvedLoan;
  };

  submission: calcAgentFunding.FundingRequestGenerator['submission'];
  // set option from response setter -> get array of things to update -> update those things
  setValue(input: SetValueFunctionInput): void;

  // ***** Submission handling *****
};

type ContextValueReadOnly = Omit<
  ContextValueMutable,
  'submission' | 'allowedActions' | 'summary'
> & {
  allowedActions: 'readOnly';
  summary: Omit<
    ContextValueMutable['summary'],
    'pendingRequests' | 'drawAvailability' | 'recycleAvailability' // omitted because they depend on currently pending requests, not stamped in for past FR
  >;
  submission: ContextValueMutable['submission'];
};

type ContextValue = ContextValueMutable | ContextValueReadOnly;

type MutableFundingRequestProperty =
  | keyof Omit<
      calcAgentFunding.FundingRequestGenerator['data'],
      'targetFundingDate'
    >
  | keyof ContextValueMutable['data'];

// all valid paths in context value for options + data properties
type StateKey = Util.Paths<Pick<ContextValueMutable, 'data'>>;

type GenericUpdatePayload<
  Key extends StateKey,
  GetValue extends GetFunction
> = {
  key: Key;
  getValue: GetValue;
};

type UpdatePayload =
  | GenericUpdatePayload<'data.pricingOptions', GetFunctionPricingOption>
  | GenericUpdatePayload<'data.recycleRequests', GetFunctionRecycleRequest>
  | GenericUpdatePayload<'data.netRecycleRequest', GetFunctionNetRecycleRequest>
  | GenericUpdatePayload<'data.drawRequests', GetFunctionDrawRequest>
  | GenericUpdatePayload<
      'data.expectedSubmission',
      GetFunctionExpectedSubmission
    >
  | GenericUpdatePayload<'data.fundingImpact', GetFunctionFundingImpact>
  | GenericUpdatePayload<'data.maturityDate', GetFunctionMaturityDate>
  | GenericUpdatePayload<'data.externalLoanId', GetFunctionExternalLoanId>
  | GenericUpdatePayload<'data.loanType', GetFunctionLoanType>
  | GenericUpdatePayload<'data.resolvedLoan', GetFunctionResolvedLoan>;

type GenericSetValueAction<
  Action extends string,
  Function extends SetFunction
> = {
  type: Action;
  payload: {
    setValue: Function;
    options: Parameters<Function>[0];
    getValueMap: Record<MutableFundingRequestProperty, UpdatePayload>;
  };
};

// expected submission + funding impact don't have setters because they will not directly be updated, just updated as a side effect of the other setters
type SetValueAction =
  | GenericSetValueAction<'setPricingOptions', SetFunctionPricingOptionsFn>
  | GenericSetValueAction<'setRecycleRequests', SetFunctionRecycleRequestsFn>
  | GenericSetValueAction<'setDrawRequests', SetFunctionDrawRequestsFn>
  | GenericSetValueAction<
      'setTargetFundingDate',
      SetFunctionTargetFundingDateFn
    >
  | GenericSetValueAction<'setExternalLoanId', SetFunctionExternalLoanIdFn>
  | GenericSetValueAction<'setMaturityDate', SetMaturityDateFn>
  | GenericSetValueAction<'setLoanType', SetFunctionLoanTypeFn>;

interface SetValueFunctionInputBase {
  type: SetValueAction['type'];
  options: SetValueAction['payload']['options'];
}

interface SetValueActionSetPricingOptionsInput
  extends SetValueFunctionInputBase {
  type: 'setPricingOptions';
  options: Extract<
    SetValueAction,
    { type: 'setPricingOptions' }
  >['payload']['options'];
}

interface SetValueActionSetRecycleRequestsInput
  extends SetValueFunctionInputBase {
  type: 'setRecycleRequests';
  options: Extract<
    SetValueAction,
    { type: 'setRecycleRequests' }
  >['payload']['options'];
}

interface SetValueActionSetDrawRequestsInput extends SetValueFunctionInputBase {
  type: 'setDrawRequests';
  options: Extract<
    SetValueAction,
    { type: 'setDrawRequests' }
  >['payload']['options'];
}

interface SetValueActionSetTargetFundingDateInput
  extends SetValueFunctionInputBase {
  type: 'setTargetFundingDate';
  options: Extract<
    SetValueAction,
    { type: 'setTargetFundingDate' }
  >['payload']['options'];
}

interface SetValueActionSetExternalLoanIdInput
  extends SetValueFunctionInputBase {
  type: 'setExternalLoanId';
  options: Extract<
    SetValueAction,
    { type: 'setExternalLoanId' }
  >['payload']['options'];
}

interface SetValueActionSetMaturityDateInput extends SetValueFunctionInputBase {
  type: 'setMaturityDate';
  options: Extract<
    SetValueAction,
    { type: 'setMaturityDate' }
  >['payload']['options'];
}

interface SetValueActionSetLoanTypeInput extends SetValueFunctionInputBase {
  type: 'setLoanType';
  options: Extract<
    SetValueAction,
    { type: 'setLoanType' }
  >['payload']['options'];
}

type SetValueFunctionInput =
  | SetValueActionSetPricingOptionsInput
  | SetValueActionSetRecycleRequestsInput
  | SetValueActionSetDrawRequestsInput
  | SetValueActionSetTargetFundingDateInput
  | SetValueActionSetExternalLoanIdInput
  | SetValueActionSetMaturityDateInput
  | SetValueActionSetLoanTypeInput;

type InitStateAction = {
  type: 'initState';
  payload: {
    newState: ContextValue;
  };
};

export type FundingRequestContextValue = {
  isLoading: boolean;
  template?: NonNullable<
    ReturnType<typeof useGetOrCreateFundingRequestTemplateQuery>['data']
  >['template'];
  creditFacility: Omit<
    ReturnType<typeof useCreditFacilitiesOrThrow>['facilities'][0],
    'implementationId'
  >;
  context: ContextValue;
  errorMessage?: string;
};

const _createMutableContext = (
  inputModeAndAllowedActions: Pick<
    ContextValueMutable,
    'mode' | 'allowedActions'
  >,
) => {
  return {
    ...inputModeAndAllowedActions,
    creditAgreementId: '',
    summary: {
      commitment: { amount: 0, currency: 'USD' as const },
      borrowingBaseAmount: 0,
      maximumAvailableAmount: 0,
      outstandingBalance: 0,
      facilityUtilization: 0,
      // TODO update defaults
      borrowingBaseReportMetadata: null,
      borrowingBaseReportMetadataForLastApprovedFundingRequest: null,
      assignedEligibleExposureSinceLastApprovedFundingRequest: null,
      pendingRequests: [
        {
          type: 'collectionsPaydown' as const,
          fundingRequestComponentId: '10000000-0000-0000-0000-000000000000',
          amount: 500_000,
          targetFundingDate: '2024-10-27' as const,
          fundingRequestId: '10000000-0000-0000-0000-000000000001',
        },
        {
          type: 'collectionsRecycle' as const,
          fundingRequestComponentId: '20000000-0000-0000-0000-000000000000',
          amount: 750_000,
          targetFundingDate: '2024-10-31' as const,
          fundingRequestId: '10000000-0000-0000-0000-000000000002',
        },
        {
          type: 'drawRequest' as const,
          fundingRequestComponentId: '30000000-0000-0000-0000-000000000000',
          amount: 1_000_000,
          targetFundingDate: '2024-10-31' as const,
          fundingRequestId: '10000000-0000-0000-0000-000000000002',
        },
      ],
      drawAvailability: 0,
      recycleAvailability: 0,
    },
    reportingMechanics: {
      fundingNotices: [],
    },
    data: {
      pricingOptions: [],
      recycleRequests: [],
      netRecycleRequest: null,
      drawRequests: [],
      maturityDate: '2024-11-30' as const,
      loanType: 'origination' as const,
      resolvedLoan: undefined,
      fundingImpact: {
        commitment: { amount: 0, currency: 'USD' as const },
        borrowingBaseAmount: {
          cash: { before: 0, after: 0 },
          receivables: { before: 0, after: 0 },
          total: { before: 0, after: 0 },
        },
        maximumAvailableAmount: { before: 0, after: 0 },
        outstandingBalance: { before: 0, after: 0 },
        drawAvailability: { before: 0, after: 0 },
        recycleAvailability: { before: 0, after: 0 },
      },
      expectedSubmission: {
        recycle: {
          global: {
            requestAmount: 0,
            constraints: [],
          },
          requests: [],
        },
        draw: {
          global: {
            requestAmount: 0,
            constraints: [],
          },
          requests: [],
        },
      },
    },
    submission: {
      getPayload: () =>
        ({
          isValid: false, // Doesn't matter here yet, TODO: migrate to this property
          payload: null,
        } as ReturnType<
          FundingRequestContextValue['context']['submission']['getPayload']
        >),
      getEditPayload: () => {
        return {
          isValid: false,
          errorMessage: 'This is not supported',
        }; // Implement type when available
      },
    },
    setValue: () => {},
  };
};

const _createReadOnlyContext = (
  inputModeAndAllowedActions: Pick<
    ContextValueReadOnly,
    'mode' | 'allowedActions'
  >,
): ContextValueReadOnly => {
  return {
    ...inputModeAndAllowedActions,
    creditAgreementId: '',
    summary: {
      commitment: { amount: 0, currency: 'USD' as const },
      borrowingBaseAmount: 0,
      maximumAvailableAmount: 0,
      outstandingBalance: 0,
      facilityUtilization: 0,
      // TODO update defaults
      borrowingBaseReportMetadata: null,
      borrowingBaseReportMetadataForLastApprovedFundingRequest: null,
      assignedEligibleExposureSinceLastApprovedFundingRequest: null,
    },
    reportingMechanics: {
      fundingNotices: [],
    },
    data: {
      pricingOptions: [],
      recycleRequests: [],
      netRecycleRequest: null,
      drawRequests: [],
      maturityDate: '2024-11-30' as const,
      loanType: 'origination' as const,
      resolvedLoan: undefined,
      fundingImpact: {
        commitment: { amount: 0, currency: 'USD' as const },
        borrowingBaseAmount: {
          cash: { before: 0, after: 0 },
          receivables: { before: 0, after: 0 },
          total: { before: 0, after: 0 },
        },
        maximumAvailableAmount: { before: 0, after: 0 },
        outstandingBalance: { before: 0, after: 0 },
        drawAvailability: { before: 0, after: 0 },
        recycleAvailability: { before: 0, after: 0 },
      },
      expectedSubmission: {
        recycle: {
          global: {
            requestAmount: 0,
            constraints: [],
          },
          requests: [],
        },
        draw: {
          global: {
            requestAmount: 0,
            constraints: [],
          },
          requests: [],
        },
      },
    },
    submission: {
      getPayload: () => ({
        isValid: false,
        payload: null,
        errors: [],
      }),
    },
    setValue: () => {},
  };
};

const _createContext = (
  inputModeAndAllowedActions:
    | Pick<ContextValueMutable, 'mode' | 'allowedActions'>
    | Pick<ContextValueReadOnly, 'mode' | 'allowedActions'>,
): ContextValue => {
  const { allowedActions } = inputModeAndAllowedActions;
  switch (allowedActions) {
    case 'mutable':
      return _createMutableContext(inputModeAndAllowedActions);
    case 'readOnly':
      return _createReadOnlyContext(inputModeAndAllowedActions);
    default:
      return finleyAssertUnreachable(allowedActions);
  }
};

const _getDefaultValue = (
  inputModeAndAllowedActions:
    | Pick<ContextValueMutable, 'mode' | 'allowedActions'>
    | Pick<ContextValueReadOnly, 'mode' | 'allowedActions'>,
): FundingRequestContextValue => {
  const defaultState = {
    isLoading: true,
    template: undefined,
    creditFacility: {
      id: '',
      name: '',
      borrowerEntityName: '',
      borrowerId: '',
      isActive: true,
      slug: '',
      type: 'creditFacility' as const,
      loanProducts: [],
    },
  };

  return {
    ...defaultState,
    context: _createContext(inputModeAndAllowedActions),
  };
};

const createSetValuePayload = ({
  fundingRequest,
  getValueMap,
  type,
  options,
}: SetValueFunctionInput & {
  fundingRequest: calcAgentFunding.FundingRequestGenerator;
  getValueMap: Record<MutableFundingRequestProperty, UpdatePayload>;
}): SetValueAction => {
  switch (type) {
    case 'setPricingOptions':
      return {
        type,
        payload: {
          setValue: fundingRequest?.data.pricingOptions.set ?? (() => []),
          getValueMap,
          options,
        },
      };

    case 'setRecycleRequests':
      return {
        type,
        payload: {
          setValue: fundingRequest?.data.recycleRequests.set ?? (() => []),
          getValueMap,
          options,
        },
      };

    case 'setDrawRequests':
      return {
        type,
        payload: {
          setValue: fundingRequest?.data.drawRequests.set ?? (() => []),
          getValueMap,
          options,
        },
      };

    case 'setTargetFundingDate':
      return {
        type,
        payload: {
          setValue: fundingRequest?.data.targetFundingDate.set ?? (() => []),
          getValueMap,
          options,
        },
      };
    case 'setExternalLoanId':
      return {
        type,
        payload: {
          setValue: fundingRequest?.data.externalLoanId.set ?? (() => []),
          getValueMap,
          options,
        },
      };
    case 'setMaturityDate':
      return {
        type,
        payload: {
          setValue: fundingRequest?.data.maturityDate.set ?? (() => []),
          getValueMap,
          options,
        },
      };
    case 'setLoanType':
      return {
        type,
        payload: {
          setValue: fundingRequest?.data.loanType.set ?? (() => []),
          getValueMap,
          options,
        },
      };
    default:
      return finleyAssertUnreachable(type);
  }
};

/**
 * Looks like duplicate, but TS can't infer the type of the payload based on type unless you explicitly return at each case
 */
const getPropertiesToUpdate = ({ type, payload }: SetValueAction) => {
  switch (type) {
    case 'setPricingOptions':
      return payload.setValue(payload.options);
    case 'setRecycleRequests':
      return payload.setValue(payload.options);
    case 'setDrawRequests':
      return payload.setValue(payload.options);
    case 'setTargetFundingDate':
      return payload.setValue(payload.options);
    case 'setExternalLoanId':
      return payload.setValue(payload.options);
    case 'setMaturityDate':
      return payload.setValue(payload.options);
    case 'setLoanType':
      return payload.setValue(payload.options);
    default:
      return finleyAssertUnreachable(type);
  }
};

/**
 * Update will take in
 *
 *  @param input UpdatePayload
 *
 * Update fn will
 * 1. getValue()
 * 2. setStateValue(key, value))
 */
const updateState = ({
  state,
  updatePayloads,
}: {
  state: ContextValueMutable;
  updatePayloads: UpdatePayload[];
}): ContextValueMutable => {
  const newState = { ...state };
  updatePayloads.forEach((property) => {
    const newProperty = property.getValue();
    _.set(newState, property.key, newProperty);
  });
  return newState;
};

const reducer = (
  state: ContextValueMutable | ContextValueReadOnly,
  action: SetValueAction | InitStateAction,
): ContextValue => {
  const { type } = action;
  switch (type) {
    case 'initState':
      return action.payload.newState;
    case 'setPricingOptions':
    case 'setRecycleRequests':
    case 'setDrawRequests':
    case 'setExternalLoanId':
    case 'setTargetFundingDate':
    case 'setMaturityDate':
    case 'setLoanType': {
      finleyAssert(
        state.allowedActions === 'mutable',
        'State must be mutable to update funding request properties',
      );
      const propertiesToUpdate = getPropertiesToUpdate(action);
      if (propertiesToUpdate.length === 0) {
        return state;
      }
      const updatePayloads = propertiesToUpdate.map(
        (property) => action.payload.getValueMap[property],
      );
      return updateState({ state, updatePayloads });
    }
    default:
      return finleyAssertUnreachable(type);
  }
};

const generateNewMutableState = ({
  mode,
  template,
  fundingRequest,
  setValue,
}: {
  mode: ContextValue['mode'];
  template: any;
  fundingRequest: calcAgentFunding.FundingRequestGenerator;
  setValue: (input: SetValueFunctionInput) => void;
}): ContextValueMutable => {
  return {
    mode,
    allowedActions: 'mutable',
    creditAgreementId: template.creditAgreementId,
    summary: fundingRequest.summary,
    reportingMechanics: template.reportingMechanics,
    data: {
      pricingOptions: fundingRequest.data.pricingOptions.get(),
      recycleRequests: fundingRequest.data.recycleRequests.get(),
      netRecycleRequest: fundingRequest.data.netRecycleRequest.get(),
      drawRequests: fundingRequest.data.drawRequests.get(),
      expectedSubmission: fundingRequest.data.expectedSubmission.get(),
      fundingImpact: fundingRequest.data.fundingImpact.get(),
      maturityDate: fundingRequest.data.maturityDate.get(),
      externalLoanId: fundingRequest.data.externalLoanId.get(),
      loanType: fundingRequest.data.loanType.get(),
      resolvedLoan: fundingRequest.data.resolvedLoan.get(),
    },
    submission: fundingRequest.submission,
    setValue,
  };
};

const generateNewReadOnlyState = ({
  mode,
  template,
  fundingRequest,
}: {
  mode: ContextValue['mode'];
  template: any;
  fundingRequest: calcAgentFunding.FundingRequestGenerator;
}): ContextValueReadOnly => {
  return {
    mode,
    allowedActions: 'readOnly',
    creditAgreementId: template.creditAgreementId,
    summary: fundingRequest.summary,
    reportingMechanics: template.reportingMechanics,
    data: {
      pricingOptions: fundingRequest.data.pricingOptions.get(),
      recycleRequests: fundingRequest.data.recycleRequests.get(),
      netRecycleRequest: fundingRequest.data.netRecycleRequest.get(),
      drawRequests: fundingRequest.data.drawRequests.get(),
      expectedSubmission: fundingRequest.data.expectedSubmission.get(),
      fundingImpact: fundingRequest.data.fundingImpact.get(),
      maturityDate: fundingRequest.data.maturityDate.get(),
      externalLoanId: fundingRequest.data.externalLoanId.get(),
      loanType: fundingRequest.data.loanType.get(),
      resolvedLoan: fundingRequest.data.resolvedLoan.get(),
    },
    submission: {
      getPayload: () => ({
        isValid: false,
        payload: null,
        errors: [],
      }),
    },
    setValue: (_input: SetValueFunctionInput) => {
      throw new Error('Cannot set value in read-only context');
    },
  };
};

// Exports
const FundingRequestContext = createContext<FundingRequestContextValue>(
  _getDefaultValue({ mode: 'create', allowedActions: 'readOnly' }),
);

/**
 * @deprecated
 *
 * Once new FR calcs consolidated, will cutover to useFundingRequestTemplateContextV1 instead of useFundingRequestTemplateContextV1
 */
export const useFundingRequestTemplateContextV1 = () =>
  useContext(FundingRequestContext);

/**
 * Will utilize "mode" to help distinguish behavior:
 * - create: create new FR that originates new loan(s)
 * - upsize: takes existing loan and creates new FR that draws on that loan
 *
 * Allowed actions will dictate if the user can edit the FR or not (mutable), and can facilitate static read only view as well.
 */
type FundingRequestTemplateProviderProps =
  | {
      mode: 'create';
      allowedActions: 'mutable';
      creditFacilityId: string;
      fundingRequestIdToDuplicate?: string;
      children: React.ReactNode;
    }
  | {
      mode: 'create';
      allowedActions: 'readOnly';
      creditFacilityId: string;
      fundingRequestId: string;
      children: React.ReactNode;
    }
  | {
      mode: 'upsize';
      allowedActions: 'mutable';
      creditFacilityId: string;
      loanId: string;
      fundingRequestIdToDuplicate?: string;
      children: React.ReactNode;
    }
  | {
      mode: 'upsize';
      allowedActions: 'readOnly';
      creditFacilityId: string;
      loanId: string;
      fundingRequestId: string;
      children: React.ReactNode;
    };

const _getGqlQueryFetchMode = (
  props: FundingRequestTemplateProviderProps,
): 'upsize' | 'create' | 'get' => {
  if (props.allowedActions === 'readOnly') {
    return 'get';
  }
  return props.mode;
};

const _getFundingRequestId = (
  props: FundingRequestTemplateProviderProps,
): string | undefined => {
  if (props.allowedActions === 'readOnly') {
    return props.fundingRequestId;
  }
  return undefined;
};

const _getLoanId = (
  props: FundingRequestTemplateProviderProps,
): string | undefined => {
  if (props.mode === 'upsize') {
    return props.loanId || undefined;
  }
  return undefined;
};

const _getFundingRequestIdToDuplicate = (
  props: FundingRequestTemplateProviderProps,
): string | undefined => {
  if (
    (props.mode === 'upsize' || props.mode === 'create') &&
    props.allowedActions === 'mutable'
  ) {
    return props.fundingRequestIdToDuplicate || undefined;
  }
  return undefined;
};

export const FundingRequestTemplateProviderV1 = (
  props: FundingRequestTemplateProviderProps,
) => {
  const defaultStateValue = useMemo(() => {
    return _getDefaultValue({
      mode: props.mode,
      allowedActions: props.allowedActions,
    });
  }, [props.mode, props.allowedActions]);
  const { facilities } = useCreditFacilitiesOrThrow();
  const [state, dispatch] = useReducer(reducer, defaultStateValue.context);

  const { creditFacilityId, children, mode, allowedActions } = props;

  const creditFacility = useMemo(() => {
    const facility = facilities.find(
      (currFacility) => currFacility.id === creditFacilityId,
    );
    finleyAssert(
      facility,
      `Failed to find credit facility with [id: ${creditFacilityId}]`,
    );

    return facility;
  }, [facilities, creditFacilityId]);

  const {
    data: fundingRequestTemplateResponse,
    loading: isLoading,
    error,
  } = useGetOrCreateFundingRequestTemplateQuery({
    variables: {
      mode: _getGqlQueryFetchMode(props),
      creditFacilityId,
      fundingRequestId: _getFundingRequestId(props),
      loanId: _getLoanId(props),
      fundingRequestIdToDuplicate: _getFundingRequestIdToDuplicate(props),
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (fundingRequestTemplateResponse?.template) {
      // need to resolve the gql return of the template w/ the db types required by BE
      const fundingRequest = calcAgentFunding.getFundingRequestGenerator({
        ...fundingRequestTemplateResponse.template,
        mode,
        allowedActions,
      } as any);

      const getValueMap: Record<
        keyof ContextValueMutable['data'],
        UpdatePayload
      > = {
        pricingOptions: {
          key: 'data.pricingOptions',
          getValue: fundingRequest?.data.pricingOptions.get ?? (() => []),
        },
        recycleRequests: {
          key: 'data.recycleRequests',
          getValue: fundingRequest?.data.recycleRequests.get ?? (() => []),
        },
        netRecycleRequest: {
          key: 'data.netRecycleRequest',
          getValue: fundingRequest?.data.netRecycleRequest.get ?? (() => null),
        },
        drawRequests: {
          key: 'data.drawRequests',
          getValue: fundingRequest?.data.drawRequests.get ?? (() => []),
        },
        fundingImpact: {
          key: 'data.fundingImpact',
          getValue:
            fundingRequest?.data.fundingImpact.get ??
            (() => defaultStateValue.context.data.fundingImpact),
        },
        expectedSubmission: {
          key: 'data.expectedSubmission',
          getValue:
            fundingRequest?.data.expectedSubmission.get ??
            (() => defaultStateValue.context.data.expectedSubmission),
        },
        maturityDate: {
          key: 'data.maturityDate',
          getValue:
            fundingRequest?.data.maturityDate.get ??
            (() => defaultStateValue.context.data.maturityDate),
        },
        externalLoanId: {
          key: 'data.externalLoanId',
          getValue:
            fundingRequest?.data.externalLoanId.get ??
            (() => defaultStateValue.context.data.externalLoanId),
        },
        loanType: {
          key: 'data.loanType',
          getValue:
            fundingRequest?.data.loanType.get ??
            (() => defaultStateValue.context.data.loanType),
        },
        resolvedLoan: {
          key: 'data.resolvedLoan',
          getValue:
            fundingRequest?.data.resolvedLoan.get ??
            (() => defaultStateValue.context.data.resolvedLoan),
        },
      };

      if (allowedActions === 'readOnly') {
        dispatch({
          type: 'initState',
          payload: {
            newState: generateNewReadOnlyState({
              mode,
              template: fundingRequestTemplateResponse.template,
              fundingRequest,
            }),
          },
        });
      } else {
        const setValue = (input: SetValueFunctionInput) => {
          dispatch(
            createSetValuePayload({
              fundingRequest,
              getValueMap,
              ...input,
            }),
          );
        };

        dispatch({
          type: 'initState',
          payload: {
            newState: generateNewMutableState({
              mode,
              template: fundingRequestTemplateResponse.template,
              fundingRequest,
              setValue,
            }),
          },
        });
      }
    }
  }, [fundingRequestTemplateResponse, mode, allowedActions, defaultStateValue]);

  return (
    <FundingRequestContext.Provider
      value={{
        context: state,
        creditFacility,
        template: fundingRequestTemplateResponse?.template,
        isLoading: Boolean(isLoading),
        // @jendevelops still need to handle errors that come out of the funding.FundingRequest method
        errorMessage: error?.message,
      }}
    >
      {children}
    </FundingRequestContext.Provider>
  );
};
